/**
 * P34 contextual Help for the flag-gated Portals workspace.
 *
 * The drawer deliberately owns no route or scope state. Opening it leaves the
 * canonical portal URL untouched, and the W1 Drawer restores focus when it
 * closes. The documentation read is used only for source/freshness truth;
 * build-matched help copy remains identified by its stable manifest ID.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useLocation, useParams } from 'react-router-dom';
import type { ReadResponse } from '../../contracts';
import apiClient from '../../services/api';
import Drawer from '../ui/Drawer';
import SourceState from '../ui/SourceState';

interface PortalDocumentation {
  portal: { id: string; name: string };
  environments: Array<{ id: string; status: string }>;
}

export interface PortalsHelpDrawerProps {
  onClose: () => void;
}

const HELP_ID = 'HLP-WARN-POR-PRICE-DRAFT-DIFF';
const tabs = ['On this page', 'Guided tasks', 'Troubleshoot', 'Reference', 'Get help'] as const;
type HelpTab = typeof tabs[number];

const content: Record<HelpTab, React.ReactNode> = {
  'On this page': (
    <div className="space-y-3 text-sm text-iflow-800">
      <h3 className="font-semibold text-iflow-900">Selected warning</h3>
      <p>Pricing draft differs from live. Review its impact and safe next step without changing the draft.</p>
    </div>
  ),
  'Guided tasks': (
    <div className="space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-iflow-900">Scope and selected warning</h3>
        <p className="mt-2 text-xs text-iflow-700">InnovateFlow / TEST / Overview • Pricing draft differs from live</p>
      </div>
      <div>
        <h3 className="mb-3 text-sm font-semibold text-iflow-900">Safe guided tasks</h3>
        <ol className="space-y-2">
          {[
            'Review pricing difference',
            'Understand affected customer cohort',
            'Open Commercial pricing draft',
            'Check margin and meter coverage',
            'Return to unchanged Overview',
          ].map((task) => (
            <li key={task} className="rounded-md border border-iflow-200 bg-iflow-50 px-4 py-3 text-sm font-medium text-iflow-900">
              {task}
            </li>
          ))}
        </ol>
      </div>
      <div className="rounded-lg border border-iflow-700 bg-iflow-100 px-4 py-4 text-sm text-iflow-900">
        <h3 className="font-semibold">Escalation</h3>
        <p className="mt-2">Owner Commercial • customer impact: Product/Legal • meter evidence: FinOps • support route: Customer Operations. Context is redacted before preview/send.</p>
      </div>
    </div>
  ),
  Troubleshoot: (
    <ul className="list-disc space-y-2 pl-5 text-sm text-iflow-800">
      <li>Divergent guidance remains visibly non-current and cannot launch a task.</li>
      <li>A Help outage never changes the page&apos;s operational eligibility.</li>
      <li>Reconfirm the selected portal, environment, warning and observed build.</li>
    </ul>
  ),
  Reference: <p className="text-sm text-iflow-800">Build-matched OKF architecture, ADR, API/schema, policy, runbook and evidence links.</p>,
  'Get help': <p className="text-sm text-iflow-800">Preview a redacted support context bundle, then escalate to Commercial or Customer Operations.</p>,
};

const PortalsHelpDrawer: React.FC<PortalsHelpDrawerProps> = ({ onClose }) => {
  const { portalId = 'innovateflow' } = useParams();
  const location = useLocation();
  const environmentId = new URLSearchParams(location.search).get('environmentId') ?? 'test';
  const [activeTab, setActiveTab] = useState<HelpTab>('Guided tasks');
  const query = useQuery({
    queryKey: ['portals', portalId, 'documentation'],
    queryFn: async () => (await apiClient.get<ReadResponse<PortalDocumentation>>(`/portals/${portalId}/documentation`)).data,
    staleTime: 60_000,
  });

  return (
    <Drawer onClose={onClose} ariaLabel="Contextual help" maxWidthClassName="max-w-3xl" panelClassName="bg-white">
      <header className="flex items-start justify-between gap-4 border-b border-iflow-200 px-7 py-5">
        <div>
          <h2 className="text-base font-semibold text-iflow-900">Help • {HELP_ID}</h2>
          <p className="mt-2 text-xs text-orange-700">Divergent • help v13 • owner Commercial • verified for build v18.4.2 on 26 Aug</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close help" className="rounded-lg border border-iflow-200 p-2 text-iflow-700 hover:bg-iflow-50">
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </header>

      <div className="flex border-b border-iflow-200 px-5" role="tablist" aria-label="Help sections">
        {tabs.map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            onClick={() => setActiveTab(tab)}
            className={`border-b-4 px-3 py-4 text-sm ${activeTab === tab ? 'border-iflow-500 font-semibold text-iflow-900' : 'border-transparent text-iflow-600 hover:text-iflow-900'}`}
          >
            {tab}
          </button>
        ))}
      </div>

      <section className="flex-1 overflow-y-auto px-7 py-7" role="tabpanel">
        {content[activeTab]}
      </section>

      <footer className="border-t border-iflow-100 px-7 py-4">
        {query.data ? (
          <SourceState
            source={query.data.source}
            observedAt={query.data.observedAt}
            completeness={query.data.freshness.completeness}
            confidence={query.data.freshness.confidence}
          />
        ) : (
          <p className="text-xs text-iflow-500" role="status">
            {query.isError ? 'Help source is unavailable; page eligibility is unchanged.' : 'Checking help source…'}
          </p>
        )}
        <p className="mt-3 text-xs text-iflow-500">
          {portalId} / {environmentId.toUpperCase()} • Opening or closing Help never changes scope, draft values, scroll position or eligibility.
        </p>
      </footer>
    </Drawer>
  );
};

export default PortalsHelpDrawer;
